import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Camera, Check, Loader2, Sparkles, User as UserIcon, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { validateUsername } from "@/lib/username";
import type { AnyRow } from "@/lib/rowTypes";

interface Props {
  onComplete: () => void;
  /** When true, the user can't dismiss this — used as a hard gate before social features. */
  blocking?: boolean;
  onSkip?: () => void;
}

const PRESET_AVATARS = [
  { id: "p1", bg: "bg-primary/15", fg: "text-primary", emoji: "🦊" },
  { id: "p2", bg: "bg-secondary/15", fg: "text-secondary", emoji: "🦉" },
  { id: "p3", bg: "bg-accent/15", fg: "text-accent", emoji: "🐺" },
  { id: "p4", bg: "bg-emerald-500/15", fg: "text-emerald-400", emoji: "🦁" },
  { id: "p5", bg: "bg-orange-500/15", fg: "text-orange-400", emoji: "🐉" },
  { id: "p6", bg: "bg-violet-500/15", fg: "text-violet-400", emoji: "🚀" },
  { id: "p7", bg: "bg-rose-500/15", fg: "text-rose-400", emoji: "💎" },
  { id: "p8", bg: "bg-cyan-500/15", fg: "text-cyan-400", emoji: "⚡" },
];

const isValidUsername = (u: string) => /^[a-zA-Z0-9_]{3,20}$/.test(u);
const isValidDisplayName = (n: string) => n.trim().length >= 2 && n.trim().length <= 40;

/**
 * Renders a tiny SVG data URL for a preset emoji avatar. We persist these
 * as `avatar_url` (data: URI) so the same UX works whether the user uploads
 * a photo or chooses a preset — no nullable mixed states downstream.
 */
function presetToDataUrl(p: typeof PRESET_AVATARS[number]): string {
  // Use a tinted circle background for the preset (HSL design tokens won't
  // resolve in a data URL, so we sample a neutral palette per index).
  const palette: Record<string, string> = {
    p1: "#3b82f6",
    p2: "#8b5cf6",
    p3: "#06b6d4",
    p4: "#10b981",
    p5: "#f97316",
    p6: "#a855f7",
    p7: "#f43f5e",
    p8: "#0ea5e9",
  };
  const color = palette[p.id] ?? "#3b82f6";
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 128 128'>
    <rect width='128' height='128' rx='64' fill='${color}22'/>
    <text x='50%' y='54%' text-anchor='middle' dominant-baseline='middle' font-size='72' font-family='Apple Color Emoji, Segoe UI Emoji, sans-serif'>${p.emoji}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

export default function OnboardingProfileSetup({ onComplete, blocking = false, onSkip }: Props) {
  const { user } = useAuth();
  const fileRef = useRef<HTMLInputElement>(null);

  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [usernameStatus, setUsernameStatus] = useState<
    "idle" | "checking" | "available" | "taken" | "invalid"
  >("idle");
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const emailFallback = user?.email?.split("@")[0] ?? "";

  // Prefill if profile already partially exists
  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name, avatar_url, username")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.display_name) setDisplayName(data.display_name);
        if (data?.avatar_url) setAvatarUrl(data.avatar_url);
        if (data?.username) {
          setUsername(data.username);
          setUsernameStatus("available");
        }
      });
  }, [user]);

  // Debounced username availability check.
  useEffect(() => {
    setUsernameError(null);
    if (!username) {
      setUsernameStatus("idle");
      return;
    }
    const v = validateUsername(username);
    if (v.ok !== true) {
      setUsernameStatus("invalid");
      setUsernameError(v.message);
      return;
    }
    setUsernameStatus("checking");
    const t = setTimeout(async () => {
      const { data, error } = await supabase.rpc("is_username_available", {
        p_username: username,
        p_user_id: user?.id ?? null,
      });
      if (error) {
        setUsernameStatus("idle");
        setUsernameError("Couldn't check availability. Try again.");
      } else if (data) {
        setUsernameStatus("available");
      } else {
        setUsernameStatus("taken");
        setUsernameError("That username is already taken. Try another.");
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username, user]);

  const handleUpload = async (file: File) => {
    if (!user) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5 MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    setUploading(true);
    const ext = file.name.split(".").pop()?.toLowerCase() || "png";
    const path = `${user.id}/avatar-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from("avatars")
      .upload(path, file, { upsert: true, cacheControl: "3600" });
    if (upErr) {
      toast.error(upErr.message);
      setUploading(false);
      return;
    }
    // Bucket is private — generate a long-lived signed URL so the preview
    // (and downstream avatar consumers) can actually render the image.
    const { data: signed, error: signErr } = await supabase.storage
      .from("avatars")
      .createSignedUrl(path, 60 * 60 * 24 * 365);
    if (signErr || !signed?.signedUrl) {
      toast.error("Photo uploaded, but preview failed. Please try again.");
      setUploading(false);
      return;
    }
    // Preload to confirm the URL actually renders before declaring success.
    await new Promise<void>((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve();
      img.src = signed.signedUrl;
    });
    setAvatarUrl(signed.signedUrl);
    setSelectedPreset(null);
    setUploading(false);
    toast.success("Photo uploaded");
  };

  const choosePreset = (p: typeof PRESET_AVATARS[number]) => {
    setSelectedPreset(p.id);
    setAvatarUrl(presetToDataUrl(p));
  };

  const canSave =
    user &&
    isValidDisplayName(displayName) &&
    Boolean(avatarUrl) &&
    usernameStatus === "available" &&
    !saving &&
    !uploading;

  const handleSave = async () => {
    if (!user) return;
    if (!isValidDisplayName(displayName)) {
      toast.error("Display name must be 2–40 characters");
      return;
    }
    if (!avatarUrl) {
      toast.error("Pick a photo or avatar");
      return;
    }
    const v = validateUsername(username);
    if (v.ok !== true) {
      toast.error(v.message);
      return;
    }
    if (usernameStatus !== "available") {
      toast.error("Pick an available username before continuing.");
      return;
    }
    setSaving(true);
    const payload: AnyRow = {
      id: user.id,
      display_name: displayName.trim(),
      avatar_url: avatarUrl,
      username,
      discoverable: true,
    };
    const { error } = await supabase.from("profiles").upsert(payload, { onConflict: "id" });
    setSaving(false);
    if (error) {
      const msg = /unique|duplicate/i.test(error.message)
        ? "That username is already taken. Try another."
        : /reserved/i.test(error.message)
        ? "That username is reserved. Please choose another."
        : error.message;
      toast.error(msg);
      return;
    }
    toast.success("Profile complete");
    onComplete();
  };

  const initials = (displayName || emailFallback || "?").slice(0, 2).toUpperCase();

  return (
    <div className="fixed inset-0 z-[210] overflow-y-auto bg-background/95 backdrop-blur-md">
      <div className="min-h-full flex items-center justify-center p-3 py-6 sm:p-4 sm:py-8">
        <div className="w-full max-w-md space-y-4">
          {!blocking && onSkip && (
            <div className="flex justify-end">
              <Button variant="ghost" size="sm" onClick={onSkip} className="text-muted-foreground">
                Skip <X className="w-3 h-3 ml-1" />
              </Button>
            </div>
          )}

          <div className="glass-card glow-border p-5 sm:p-7 space-y-5 animate-scale-in">
            <div className="text-center space-y-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                <Sparkles className="w-3 h-3 text-primary" />
                <span className="text-[10px] uppercase tracking-wider text-primary font-semibold">
                  One last step
                </span>
              </div>
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-foreground">
                Set up your identity
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Friends and challenges use this. Pick a name and an avatar — you can change it later.
              </p>
            </div>

            {/* Avatar preview + upload */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                <Avatar className="w-24 h-24 border-2 border-primary/30 shadow-glow">
                  {avatarUrl && <AvatarImage src={avatarUrl} alt="Your avatar" />}
                  <AvatarFallback className="bg-primary/10 text-primary font-heading font-bold text-xl">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-primary text-primary-foreground border-2 border-background flex items-center justify-center shadow-lg hover:bg-primary/90 transition"
                  aria-label="Upload photo"
                  disabled={uploading}
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleUpload(f);
                    e.currentTarget.value = "";
                  }}
                />
              </div>

              <div className="w-full">
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider text-center mb-2">
                  …or pick a preset
                </p>
                <div className="grid grid-cols-8 gap-1.5">
                  {PRESET_AVATARS.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => choosePreset(p)}
                      className={cn(
                        "aspect-square rounded-lg flex items-center justify-center text-lg sm:text-xl border transition",
                        p.bg,
                        selectedPreset === p.id
                          ? "border-primary ring-2 ring-primary/40"
                          : "border-border hover:border-primary/40",
                      )}
                      aria-label={`Preset avatar ${p.id}`}
                    >
                      {p.emoji}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Display name */}
            <div className="space-y-1.5">
              <Label htmlFor="display-name" className="text-xs font-medium text-foreground">
                Display name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="display-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="What should friends call you?"
                maxLength={40}
                className="h-10"
              />
              <p className="text-[10px] text-muted-foreground">2–40 characters. Visible to friends.</p>
            </div>

            {/* Username */}
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-xs font-medium text-foreground">
                Username <span className="text-destructive">*</span>
              </Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground pointer-events-none">
                  @
                </span>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.replace(/\s/g, ""))}
                  placeholder="yourhandle"
                  maxLength={20}
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck={false}
                  className="h-10 pl-7"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2">
                  {usernameStatus === "checking" && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
                  {usernameStatus === "available" && <Check className="w-4 h-4 text-emerald-400" />}
                  {(usernameStatus === "taken" || usernameStatus === "invalid") && (
                    <X className="w-4 h-4 text-destructive" />
                  )}
                </span>
              </div>
              <p
                className={cn(
                  "text-[10px]",
                  usernameError
                    ? "text-destructive"
                    : usernameStatus === "available"
                    ? "text-emerald-400"
                    : "text-muted-foreground",
                )}
              >
                {usernameError ??
                  (usernameStatus === "available"
                    ? "Available!"
                    : "3–20 characters. Letters, numbers, underscores, periods.")}
              </p>
            </div>

            <Button onClick={handleSave} disabled={!canSave} className="w-full h-11">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserIcon className="w-4 h-4 mr-1.5" /> Save & Continue</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
